import {
  TGroupTreeState,
  TGroupTreeStateBlob,
  TLastGroup,
} from '@/shared/features/groups/storage/group-tree-storage/group-tree-storage.types';

// Both apps have a group tree of their own; one key would let each overwrite
// the other's folders wherever they are served from the same origin.
const STORAGE_KEY = __IS_BACKOFFICE__
  ? 'sda:backoffice-group-tree'
  : 'sda:group-tree';
const VERSION = 1;

const EMPTY_BLOB: TGroupTreeStateBlob = { v: VERSION, tenants: {} };

const readBlob = (): TGroupTreeStateBlob => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_BLOB;

    const blob = JSON.parse(raw) as TGroupTreeStateBlob;
    if (blob?.v !== VERSION || !blob.tenants) return EMPTY_BLOB;

    return blob;
  } catch {
    // Reopening where you left off is a convenience, never correctness.
    return EMPTY_BLOB;
  }
};

const writeState = (tenantId: string, patch: TGroupTreeState): void => {
  const blob = readBlob();

  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...blob,
        tenants: {
          ...blob.tenants,
          [tenantId]: { ...blob.tenants[tenantId], ...patch },
        },
      }),
    );
  } catch {
    // Quota exceeded or storage blocked -- non-fatal, just skip remembering.
  }
};

export const readOpenGroups = (tenantId: string): string[] => {
  const openGroupIds = readBlob().tenants[tenantId]?.open;

  return Array.isArray(openGroupIds) ? openGroupIds : [];
};

export const writeOpenGroups = (tenantId: string, open: string[]): void =>
  writeState(tenantId, { open });

export const readLastGroup = (tenantId: string): TLastGroup | undefined => {
  const lastGroup = readBlob().tenants[tenantId]?.lastGroup;

  return lastGroup?.groupId && lastGroup?.path ? lastGroup : undefined;
};

export const writeLastGroup = (tenantId: string, lastGroup: TLastGroup): void =>
  writeState(tenantId, { lastGroup });

export const readTreeCollapsed = (tenantId: string): boolean =>
  readBlob().tenants[tenantId]?.isTreeCollapsed === true;

export const writeTreeCollapsed = (
  tenantId: string,
  isTreeCollapsed: boolean,
): void => writeState(tenantId, { isTreeCollapsed });

export const readTreeWidthRatio = (tenantId: string): number | undefined => {
  const ratio = readBlob().tenants[tenantId]?.treeWidthRatio;

  return typeof ratio === 'number' && ratio > 0 ? ratio : undefined;
};

export const writeTreeWidthRatio = (
  tenantId: string,
  treeWidthRatio: number,
): void => writeState(tenantId, { treeWidthRatio });
